import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Package, User as UserIcon, ShieldCheck } from "lucide-react";
import api, { eur, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function Account() {
  const { user, loading, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/connexion");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        phone: user.phone || "",
        address: user.address || "",
        city: user.city || "",
        postal_code: user.postal_code || "",
        country: user.country || "France",
      });
      api
        .get("/orders")
        .then(({ data }) => setOrders(data))
        .finally(() => setOrdersLoading(false));
    }
  }, [user]);

  if (loading || !user) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", form);
      setUser(data);
      toast.success("Profil mis à jour");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-6 md:px-12 pt-8 md:pt-12 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-10">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-ash mb-2">Espace client</p>
          <h1 className="font-serif text-4xl md:text-5xl text-ink tracking-tight">Bonjour, {user.name?.split(" ")[0]}</h1>
        </div>
        <button
          data-testid="logout-btn"
          onClick={() => { logout(); navigate("/"); }}
          className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-graphite hover:text-ink transition-colors"
        >
          <LogOut className="h-4 w-4" /> Déconnexion
        </button>
      </div>

      {user.role === "admin" && (
        <Link
          to="/admin"
          data-testid="goto-admin-btn"
          className="flex items-center justify-between gap-4 bg-ink text-cream rounded-sm p-5 mb-8 hover:bg-[#2C2B29] transition-colors"
        >
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-6 w-6 text-gold" strokeWidth={1.5} />
            <div>
              <p className="font-sans font-semibold text-sm">Accéder à l'administration</p>
              <p className="text-cream/60 text-xs mt-0.5">Gérer les produits et les commandes.</p>
            </div>
          </div>
          <span className="text-xs uppercase tracking-[0.15em] text-gold">Ouvrir →</span>
        </Link>
      )}

      <Tabs defaultValue="orders">
        <TabsList className="bg-sand rounded-sm mb-8">
          <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-sm data-[state=active]:bg-ink data-[state=active]:text-cream uppercase tracking-wider text-xs">
            <Package className="h-4 w-4 mr-2" /> Commandes
          </TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile" className="rounded-sm data-[state=active]:bg-ink data-[state=active]:text-cream uppercase tracking-wider text-xs">
            <UserIcon className="h-4 w-4 mr-2" /> Profil
          </TabsTrigger>
        </TabsList>

        <TabsContent value="orders">
          {ordersLoading ? (
            <div className="space-y-4">
              {[1, 2].map((i) => <div key={i} className="h-28 bg-sand animate-pulse rounded-sm" />)}
            </div>
          ) : orders.length === 0 ? (
            <div data-testid="no-orders" className="text-center py-16 bg-sand rounded-sm">
              <Package className="h-10 w-10 text-stone mx-auto" strokeWidth={1} />
              <p className="text-graphite mt-4">Aucune commande pour le moment.</p>
              <Link to="/boutique" className="inline-block mt-5 bg-ink text-cream px-6 py-3 text-xs uppercase tracking-[0.15em]">
                Découvrir la boutique
              </Link>
            </div>
          ) : (
            <div className="space-y-5">
              {orders.map((o) => (
                <div key={o.id} data-testid={`order-${o.id.slice(0, 8)}`} className="border border-stone rounded-sm p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-xs uppercase tracking-[0.15em] text-ash">Commande #{o.id.slice(0, 8)}</p>
                      <p className="text-sm text-graphite mt-1">{new Date(o.created_at).toLocaleDateString("fr-FR")}</p>
                    </div>
                    <span className="text-xs uppercase tracking-wider bg-gold/20 text-copper px-3 py-1.5 rounded-sm">
                      {o.status === "confirmed" ? "Confirmée" : o.status}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {o.items?.map((i) => (
                      <div key={i.product_id} className="flex items-center gap-3">
                        <img src={i.image} alt={i.name} className="h-12 w-10 object-cover bg-sand rounded-sm" />
                        <span className="flex-1 text-sm text-ink">{i.name} × {i.quantity}</span>
                        <span className="font-mono text-sm text-graphite">{eur(i.line_total)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between border-t border-stone mt-4 pt-3 font-mono text-ink">
                    <span>Total</span><span>{eur(o.total)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="profile">
          <form onSubmit={save} className="max-w-lg space-y-5">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-ash">Email</label>
              <Input value={user.email} disabled className="mt-2 h-12 rounded-sm border-stone bg-sand" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-ash">Nom complet</label>
              <Input data-testid="profile-name" value={form.name} onChange={set("name")} className="mt-2 h-12 rounded-sm border-stone bg-white" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-ash">Téléphone</label>
                <Input data-testid="profile-phone" value={form.phone} onChange={set("phone")} className="mt-2 h-12 rounded-sm border-stone bg-white" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-ash">Pays</label>
                <Input data-testid="profile-country" value={form.country} onChange={set("country")} className="mt-2 h-12 rounded-sm border-stone bg-white" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-ash">Adresse</label>
              <Input data-testid="profile-address" value={form.address} onChange={set("address")} className="mt-2 h-12 rounded-sm border-stone bg-white" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-ash">Ville</label>
                <Input data-testid="profile-city" value={form.city} onChange={set("city")} className="mt-2 h-12 rounded-sm border-stone bg-white" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-ash">Code postal</label>
                <Input data-testid="profile-postal" value={form.postal_code} onChange={set("postal_code")} className="mt-2 h-12 rounded-sm border-stone bg-white" />
              </div>
            </div>
            <button data-testid="save-profile-btn" type="submit" disabled={saving} className="bg-ink text-cream px-8 py-4 uppercase tracking-[0.15em] text-xs hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
              {saving ? "Enregistrement..." : "Enregistrer"}
            </button>
          </form>
        </TabsContent>
      </Tabs>
    </div>
  );
}
